import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router'
import { type SearchableUser } from '@/features/iam/users/SearchableUser'
import { VirtualTableContainer } from '@/components/VirtualTableContainer'
import { useFeatures } from '@/contexts/root-store-context'
import { type ClientUserManagementViewModel } from '@/features/client-user-management/ClientUserManagement/ClientUserManagementViewModel'
import {
  ClientUserManagementTable,
  ClientUserManagementTableHeaderRow,
} from '@/features/client-user-management/components/ClientUserManagementTable'
import { ClientUserManagementTableControlsContainer } from '@/features/client-user-management/components/ClientUserManagementTableControlsContainer'
import { ClientUserManagementTableRowContainer } from '@/features/client-user-management/components/ClientUserManagementTableRow/ClientUserManagementTableRowContainer'

export interface ClientUserManagementTableContainerProps {
  vm: ClientUserManagementViewModel
}

export const ClientUserManagementTableContainer = observer(
  function ClientUserManagementTableContainer({
    vm,
  }: ClientUserManagementTableContainerProps) {
    const navigate = useNavigate()
    const { shellInfo } = useFeatures(({ shell }) => ({
      shellInfo: shell.shellInfo,
    }))

    const visitUserDetailsLink = (user: SearchableUser) =>
      navigate(vm.getUserDetailsLinkForUser(user))

    return (
      <ClientUserManagementTable
        totalUsers={vm.userSearch.totalUsers}
        isShowingInactiveUsers={vm.userSearch.userInactiveFilter.value}
        onInactiveChange={vm.userSearch.userInactiveFilter.change}
        controls={<ClientUserManagementTableControlsContainer vm={vm} />}
        showEmptyState={vm.userSearch.shouldShowEmptyState}
        isSearchFilterApplied={vm.userSearch.isSearchFilterApplied}
        corporateMode={shellInfo.corporateMode}
        onClearSearchFilter={vm.userSearch.clearFilters}
        table={
          <VirtualTableContainer
            overscan={50}
            scroller={vm.userSearch.scrollProvider}
            data={vm.userSearch.users}
            headerContent={() => <HeaderRowContainer vm={vm} />}
            itemContent={(_index, user) => (
              <ClientUserManagementTableRowContainer
                key={user.publicId}
                user={user}
              />
            )}
            onPressItemRow={visitUserDetailsLink}
          />
        }
      />
    )
  },
)

interface HeaderRowContainerProps {
  vm: ClientUserManagementViewModel
}

const HeaderRowContainer = observer(function HeaderRowContainer({
  vm,
}: HeaderRowContainerProps) {
  return (
    <ClientUserManagementTableHeaderRow
      columns={vm.columns.map((col) => ({
        id: col.id,
        header: col.header,
        sortable: col.sortable,
        visible: col.visible,
      }))}
    />
  )
})
